// CurveEditor — chart-as-control React island for a `curve_editor` group.
//
// The chart shows two line series: baseline (neutral) and reform (brand
// primary). The reform line is computed locally from the baseline values
// times (1 + delta/100), where delta is the slider's current value. Drag
// the slider, watch the curve deform. No engine call fires here; the
// page-level Simulate button gates that.
//
// The component writes its current delta to Shiny via setInputValue, so
// the server-side panel reactive at `input[[group$id]]` reads the same
// value scalar_multiplier would have produced. The existing
// panels_to_modifications transform handles the literal-values expansion
// at engine-call time — the React side stays in "intent" terms (the
// user-facing percentage delta).
//
// Layout: chart above, slider below, caption beneath the slider listing
// the backend params the slider scales. The wrapping card chrome (title,
// status badge, About-this-instrument disclosure) is Shiny's job; the
// island fills only the body slot.

import { useMemo, useState } from "react";
import type { EChartsOption } from "echarts";
import { Chart } from "../chart";
import { DeltaSlider } from "./delta_slider";
import type { CurveEditorProps } from "./types";

export function CurveEditor({
  inputId,
  label,
  instrument,
  description,
  defaultValue,
  min,
  max,
  step,
  xAxisData,
  yAxisData,
  xAxisLabel,
  yAxisLabel,
}: CurveEditorProps): JSX.Element {
  // The DeltaSlider below owns the value, the Shiny write, and the reset
  // affordances. CurveEditor only mirrors the current delta (via the
  // slider's onValueChange) to deform the decoration chart live.
  const [delta, setDelta] = useState<number>(defaultValue);

  const reformY = useMemo(
    () => yAxisData.map((v) => v * (1 + delta / 100)),
    [yAxisData, delta],
  );

  // Lock the y-axis to the slider's reachable range so it never
  // re-inks during a drag. Bounds: y at slider-min (lower envelope)
  // and y at slider-max (upper envelope). With the ±100% range this
  // is 0 to 2× baseline_max — a stable, legible frame.
  const yBounds = useMemo(() => {
    const baselineMax = Math.max(...yAxisData);
    const baselineMin = Math.min(...yAxisData);
    // Round the +100% envelope up to a clean number so the gridlines are round
    // values (e.g. 4000, gridlines 0/1000/2000/3000/4000) rather than the exact
    // 3568.08. The topmost gridline is unlabelled (showMaxLabel: false).
    const rawMax = baselineMax * (1 + max / 100);
    const step = rawMax > 0 ? Math.pow(10, Math.floor(Math.log10(rawMax))) : 1;
    return {
      min: Math.min(0, baselineMin * (1 + min / 100)),
      max: rawMax > 0 ? Math.ceil(rawMax / step) * step : 1,
    };
  }, [yAxisData, min, max]);

  const option = useMemo<EChartsOption>(
    () => ({
      // Bottom padding gives the category ticks + axis name room;
      // top padding gives the legend its corner.
      grid: { left: 64, right: 16, top: 36, bottom: 36 },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "line" },
        valueFormatter: (v: unknown) =>
          typeof v === "number" ? `€${Math.round(v).toLocaleString()}` : String(v),
      },
      // Compact legend in the top-right. The chart shape carries most
      // of the story; the legend just disambiguates which line is
      // which, so it earns its keep at small size in a corner.
      legend: {
        data: ["Baseline", "Reform"],
        right: 0,
        top: 0,
        itemWidth: 14,
        itemHeight: 8,
        textStyle: { fontSize: 11, color: "#4a4a4f" },
        itemGap: 12,
      },
      xAxis: {
        type: "category",
        // Sized to visually match the BracketEditor (D3 SVG) axis
        // labels, which render at ~13 px on screen at the shared
        // 480 px chart width cap. Echarts renders in canvas pixel
        // space (no viewBox scaling), so the value is set fixed at
        // 13 to match.
        name: xAxisLabel,
        nameLocation: "end",
        nameGap: 8,
        nameTextStyle: { fontSize: 13, color: "#6e6e70" },
        data: xAxisData.map((v) => String(v)),
        axisLabel: { fontSize: 13 },
      },
      yAxis: {
        type: "value",
        name: yAxisLabel,
        nameLocation: "end",
        nameGap: 8,
        nameTextStyle: { fontSize: 13, color: "#6e6e70" },
        // Locked to the slider's reachable range (rounded) — a stable frame
        // while dragging. The max label is hidden so the rounded ceiling isn't
        // labelled; its gridline still shows (echarts draws one at the axis
        // top), which is fine.
        min: yBounds.min,
        max: yBounds.max,
        axisLabel: { fontSize: 13, showMaxLabel: false },
      },
      series: [
        {
          name: "Baseline",
          type: "line",
          data: yAxisData,
          color: "#a8a4b5",
          // No per-point markers: this is a decorative schedule curve scaled
          // uniformly by the slider, not a point-by-point editor — dots at
          // every CV bracket wrongly imply each is individually draggable.
          // A symbol still appears on hover (emphasis) to read a bracket's
          // value. (Per-bracket editing was considered and dropped: #137 —
          // the uniform scale is the final design.)
          symbol: "circle",
          symbolSize: 6,
          showSymbol: false,
          smooth: false,
          lineStyle: { width: 2 },
        },
        {
          name: "Reform",
          type: "line",
          data: reformY,
          color: "#5e4a97",
          symbol: "circle",
          symbolSize: 7,
          showSymbol: false,
          smooth: false,
          lineStyle: { width: 3 },
        },
      ],
    }),
    [xAxisData, yAxisData, reformY, xAxisLabel, yAxisLabel, yBounds],
  );

  return (
    <div className="beamm-curve-editor" data-testid={`curve-editor-${inputId}`}>
      {/*
        notMerge=false + lazyUpdate=true lets echarts diff the new
        option against the previous one and patch only the series
        data, instead of re-initialising the whole chart on every
        slider tick. With the axis bounds already locked by yBounds,
        this is what makes the axis sit still during a drag.
      */}
      <Chart
        option={option}
        height="240px"
        notMerge={false}
        lazyUpdate={true}
      />
      <DeltaSlider
        inputId={inputId}
        label={label}
        instrument={instrument}
        description={description}
        defaultValue={defaultValue}
        min={min}
        max={max}
        step={step}
        postfix="%"
        onValueChange={setDelta}
      />
    </div>
  );
}
